import { gradient } from 'atoms/colors/colors';

import { keyExistsOnObject } from 'utils/typeUtils';

/**
 * @param hexString a valid hex string with or without the leading #
 * @returns an array with matched [RRR, BBB, GGG] values
 */
export const hexToRGB = (hexString: string) => {
  // Remove the '#' character if it's present in the hexString
  const hex = hexString.replace(/^#/, '');

  // Ensure the hex string is either 3 or 6 characters long
  if (hex.length !== 3 && hex.length !== 6) {
    throw new Error('You must provide a valid hex string with 3 or 6 characters.');
  }

  // Convert the hex string to RGB format
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }

  // Create the RGB color string
  const rgbString = `rgb(${r}, ${g}, ${b})`;

  return rgbString;
};
/**
 * @param string any string, including ones already in camelCase
 * @returns the string in camelCase form
 */
export const toCamelCase = (string: string) =>
  string
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, '');

/**
 * @param id any string, including special characters
 * @returns an all lowercase string with spaces replaced by hyphens
 */
export const stringToKebabCase = (id: string) =>
  id
    .toLowerCase()
    .replace(/[^A-Za-z0-9 ]/g, '')
    .trim()
    .replace(/\s+/g, '-');

/**
 * @param typename component content type, @param name the name of the background selected in CMS, @param width the width of the page
 * @returns `backgroundPath`: a file path corresponding to the appropriate static image, `backgroundIsDark`: boolean for making text white if background is dark
 */
export const getBackground = (typename = '', name: string | null = 'light') => {
  if (!typename || !name || name === 'light' || name === 'white' || name === 'blue-light' || name === 'foam') {
    return {
      backgroundIsDark: false,
    };
  }

  return {
    backgroundIsDark: true,
  };
};

/**
 * @param value any string
 * @returns if string is valid gradient atom, returns `gradient`. if not, `url(inputString)`
 */
export const parseBackgroundGradient = (value = '') => {
  const gradientName = value.split('-')[1];

  return keyExistsOnObject(gradient, gradientName) ? gradient[gradientName] : `url(${value})`;
};

/**
 * @param dateString any string
 * @returns formatted Date
 */
export const formatDate = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00`);
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };

  return date.toLocaleDateString('en-US', options);
};

/**
 * @param url any URL string
 * @returns youtube ID
 */
export const getYoutubeId = (url: string) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url?.match(regExp);

  return match && match[7].length === 11 ? match[7] : false;
};

/**
 * @param text any text string
 * @returns reading time
 */
export const getReadingTime = (text: string) => {
  const wpm = 225;
  const words = text?.trim()?.split(/\s+/)?.length;
  const time = Math.ceil(words / wpm);

  return `${time} ${time > 1 ? 'minutes' : 'minute'}`;
};

/**
 * @param cname cookie name
 * @returns cookie value
 */
export const getCookie = (cname: string) => {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  if (decodedCookie) {
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
  }

  return '';
};
